import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command as CommandPrimitive } from 'cmdk'
import {
  Building2,
  ChefHat,
  Loader2,
  Package,
  Receipt,
  Search,
  UserPlus,
  Wheat,
} from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../lib/shadcn/dialog'
import { Command, CommandGroup, CommandItem, CommandList } from '../../lib/shadcn/command'
import { Kbd } from '../../lib/shadcn/kbd'
import { toast } from '../../lib/shadcn/sonner'
import { useSearchHub } from '../../hooks/backend/search'
import { useGetIngredientCategories, useGetIngredients } from '../../hooks/backend/recipes'
import { useGetProductsCached } from '../../hooks/cachedQueries'
import ProductFormDialog from './ProductFormDialog'
import RecipeDialog from './RecipeDialog'
import IngredientFormDialog, { type IngredientWithComponents } from './IngredientFormDialog'
import type { IngredientCategory } from './IngredientCategoriesDialog'
import type { Product } from './ProductTable'
import {
  INGREDIENTS_CHANGED,
  OPEN_HUB_SEARCH,
  PRODUCTS_CHANGED,
  openHubSearch,
} from '../../utils/hubSearchEvents'

// Header search (Ctrl+K / Cmd+K): one box that finds orders, customers, leads,
// products, recipes and ingredients, and opens the one you pick. Orders,
// customers and leads open their page; products, recipes and ingredients open
// their dialog right where you are.

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const SHORTCUT = isMac ? '⌘K' : 'Ctrl K'

interface SearchOrder {
  salesorder_number: string
  customer: string | null
  customer_id: string | null
  order_date: string | null
  status: string | null
  invoice_number: string | null
  on_orders_page: boolean
}
interface SearchCustomer {
  customer_id: string
  name: string
  company_name: string | null
  email: string | null
  active: boolean
}
interface SearchLead {
  id: number
  name: string
  email: string | null
  stage: string | null
}
interface SearchProduct {
  sku: string
  item_name: string | null
  label_name: string | null
}
interface SearchRecipe {
  label_name: string
  sku: string | null
}
interface SearchIngredient {
  id: string
  name: string
  category_name: string | null
}
interface SearchResults {
  q: string
  orders: SearchOrder[]
  customers: SearchCustomer[]
  leads: SearchLead[]
  products: SearchProduct[]
  recipes: SearchRecipe[]
  ingredients: SearchIngredient[]
}

type Target =
  | { kind: 'product'; product: Product; tab?: 'details' | 'recipe' }
  | { kind: 'recipe'; flavorName: string }
  | { kind: 'ingredient'; ingredient: IngredientWithComponents }

const formatDate = (iso: string | null) => {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The header's search box (wide screens) or search icon (narrower screens). */
export function HubSearchButton({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <button
        type="button"
        aria-label="Search"
        title={`Search (${SHORTCUT})`}
        onClick={openHubSearch}
        className="p-2 max-md:p-2.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <Search className="w-5 h-5" />
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={openHubSearch}
      className="flex items-center gap-2 w-56 h-9 px-3 rounded-md border border-input bg-background text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
    >
      <Search className="w-4 h-4 shrink-0" />
      <span className="flex-1 text-left">Search…</span>
      <Kbd>{SHORTCUT}</Kbd>
    </button>
  )
}

export default function HubSearch() {
  const navigate = useNavigate()
  const searchHub = useSearchHub()
  const getProducts = useGetProductsCached()
  const getIngredients = useGetIngredients()
  const getCategories = useGetIngredientCategories()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [searching, setSearching] = useState(false)
  const [opening, setOpening] = useState(false)
  const [target, setTarget] = useState<Target | null>(null)
  const [targetOpen, setTargetOpen] = useState(false)
  const requestId = useRef(0)

  // Ctrl+K / Cmd+K anywhere, plus the header and phone-menu buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    const onOpen = () => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener(OPEN_HUB_SEARCH, onOpen)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener(OPEN_HUB_SEARCH, onOpen)
    }
  }, [])

  // Search as you type, a moment after the last key. Answers to older queries
  // are dropped so a slow reply can't replace a newer one.
  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      requestId.current++
      setResults(null)
      setSearching(false)
      return
    }
    setSearching(true)
    const id = ++requestId.current
    const timer = window.setTimeout(async () => {
      try {
        const data = (await searchHub.trigger({ q })) as SearchResults
        if (id === requestId.current) setResults(data)
      } catch {
        if (id === requestId.current) {
          setResults(null)
          toast.error('Search failed. Please try again.')
        }
      } finally {
        if (id === requestId.current) setSearching(false)
      }
    }, 200)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      setResults(null)
    }
  }

  const close = () => handleOpenChange(false)

  const loadProduct = useCallback(
    async (sku: string): Promise<Product | null> => {
      const cached = (getProducts.data as Product[] | null) ?? []
      const hit = cached.find(p => p.sku === sku)
      if (hit) return hit
      const fresh = ((await getProducts.trigger()) as Product[] | null) ?? []
      return fresh.find(p => p.sku === sku) ?? null
    },
    [getProducts],
  )

  // Opens a dialog once the search box has closed, so the two don't fight
  // over focus.
  const showTarget = (t: Target) => {
    close()
    setTarget(t)
    window.setTimeout(() => setTargetOpen(true), 0)
  }

  const runOpen = async (fn: () => Promise<void>) => {
    setOpening(true)
    try {
      await fn()
    } catch {
      toast.error('Could not open it. Please try again.')
    } finally {
      setOpening(false)
    }
  }

  const pickOrder = (o: SearchOrder) => {
    close()
    if (o.on_orders_page) {
      navigate(`/orders?order=${encodeURIComponent(o.salesorder_number)}`)
    } else if (o.customer_id) {
      toast.info(`${o.salesorder_number} was cleared from the Orders page. Showing ${o.customer ?? 'the customer'} instead.`)
      navigate(`/customers/${encodeURIComponent(o.customer_id)}`)
    } else {
      toast.info(`${o.salesorder_number} was cleared from the Orders page.`)
    }
  }

  const pickProduct = (p: SearchProduct) =>
    runOpen(async () => {
      const product = await loadProduct(p.sku)
      if (!product) {
        toast.error(`Could not find ${p.item_name ?? p.sku}.`)
        return
      }
      showTarget({ kind: 'product', product })
    })

  const pickRecipe = (r: SearchRecipe) =>
    runOpen(async () => {
      const product = r.sku ? await loadProduct(r.sku) : null
      // A product's recipe is on the Recipe tab of its product dialog.
      if (product) showTarget({ kind: 'product', product, tab: 'recipe' })
      else showTarget({ kind: 'recipe', flavorName: r.label_name })
    })

  const pickIngredient = (i: SearchIngredient) =>
    runOpen(async () => {
      const [list] = await Promise.all([
        getIngredients.trigger() as Promise<IngredientWithComponents[] | null>,
        getCategories.trigger({}),
      ])
      const ingredient = (list ?? []).find(x => String(x.id) === i.id)
      if (!ingredient) {
        toast.error(`Could not find ${i.name}.`)
        return
      }
      showTarget({ kind: 'ingredient', ingredient })
    })

  const r = results
  const total = r
    ? r.orders.length + r.customers.length + r.leads.length + r.products.length + r.recipes.length + r.ingredients.length
    : 0
  const typed = query.trim().length >= 2

  const itemClass = 'gap-3 py-2 cursor-pointer'
  const sub = (text: string) => <span className="text-xs text-muted-foreground truncate">{text}</span>

  const ingredientsList = (getIngredients.data as IngredientWithComponents[] | null) ?? []
  const categories = (getCategories.data as IngredientCategory[] | null) ?? []

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="overflow-hidden !p-0 gap-0 sm:max-w-xl top-[15%] translate-y-0 data-[state=open]:slide-in-from-top-4">
          <DialogTitle className="sr-only">Search</DialogTitle>
          <DialogDescription className="sr-only">
            Find an order, customer, lead, product, recipe or ingredient.
          </DialogDescription>
          <Command shouldFilter={false} loop className="flex flex-col min-h-0">
            <div className="flex items-center border-b px-3 pr-10" cmdk-input-wrapper="">
              {searching || opening ? (
                <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin opacity-60" />
              ) : (
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              )}
              <CommandPrimitive.Input
                value={query}
                onValueChange={setQuery}
                autoFocus
                placeholder="Search orders, customers, products, recipes, ingredients…"
                className="flex h-12 w-full bg-transparent py-3 text-base sm:text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <CommandList className="max-h-[min(60vh,440px)] max-sm:max-h-none max-sm:flex-1">
              {!typed && (
                <p className="py-8 px-4 text-center text-sm text-muted-foreground">
                  Type an order number, customer or lead name, product name or SKU, recipe or ingredient.
                </p>
              )}
              {typed && r && r.q === query.trim() && total === 0 && !searching && (
                <p className="py-8 px-4 text-center text-sm text-muted-foreground">
                  Nothing matches “{query.trim()}”.
                </p>
              )}
              {typed && r && (
                <>
                  {r.orders.length > 0 && (
                    <CommandGroup heading="Orders">
                      {r.orders.map(o => (
                        <CommandItem
                          key={`order:${o.salesorder_number}`}
                          value={`order:${o.salesorder_number}`}
                          onSelect={() => pickOrder(o)}
                          className={itemClass}
                        >
                          <Receipt className="text-muted-foreground" />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">
                              {o.salesorder_number}
                              {o.customer ? ` · ${o.customer}` : ''}
                            </span>
                            {sub(
                              [
                                formatDate(o.order_date),
                                o.invoice_number ? `Invoice ${o.invoice_number}` : null,
                                o.on_orders_page ? null : 'Cleared',
                              ]
                                .filter(Boolean)
                                .join(' · '),
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {r.customers.length > 0 && (
                    <CommandGroup heading="Customers">
                      {r.customers.map(c => (
                        <CommandItem
                          key={`customer:${c.customer_id}`}
                          value={`customer:${c.customer_id}`}
                          onSelect={() => {
                            close()
                            navigate(`/customers/${encodeURIComponent(c.customer_id)}`)
                          }}
                          className={itemClass}
                        >
                          <Building2 className="text-muted-foreground" />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">{c.name}</span>
                            {sub(
                              [
                                c.company_name && c.company_name !== c.name ? c.company_name : null,
                                c.email,
                                c.active ? null : 'Inactive',
                              ]
                                .filter(Boolean)
                                .join(' · '),
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {r.leads.length > 0 && (
                    <CommandGroup heading="Leads">
                      {r.leads.map(l => (
                        <CommandItem
                          key={`lead:${l.id}`}
                          value={`lead:${l.id}`}
                          onSelect={() => {
                            close()
                            navigate(`/crm/leads/${l.id}`)
                          }}
                          className={itemClass}
                        >
                          <UserPlus className="text-muted-foreground" />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">{l.name}</span>
                            {sub([l.stage, l.email].filter(Boolean).join(' · '))}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {r.products.length > 0 && (
                    <CommandGroup heading="Products">
                      {r.products.map(p => (
                        <CommandItem
                          key={`product:${p.sku}`}
                          value={`product:${p.sku}`}
                          onSelect={() => void pickProduct(p)}
                          className={itemClass}
                        >
                          <Package className="text-muted-foreground" />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">{p.item_name ?? p.sku}</span>
                            {sub(`SKU ${p.sku}`)}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {r.recipes.length > 0 && (
                    <CommandGroup heading="Recipes">
                      {r.recipes.map(rec => (
                        <CommandItem
                          key={`recipe:${rec.label_name}`}
                          value={`recipe:${rec.label_name}`}
                          onSelect={() => void pickRecipe(rec)}
                          className={itemClass}
                        >
                          <ChefHat className="text-muted-foreground" />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">{rec.label_name}</span>
                            {sub(rec.sku ? 'Recipe' : 'Recipe-only flavor')}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {r.ingredients.length > 0 && (
                    <CommandGroup heading="Ingredients">
                      {r.ingredients.map(i => (
                        <CommandItem
                          key={`ingredient:${i.id}`}
                          value={`ingredient:${i.id}`}
                          onSelect={() => void pickIngredient(i)}
                          className={itemClass}
                        >
                          <Wheat className="text-muted-foreground" />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">{i.name}</span>
                            {i.category_name && sub(i.category_name)}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {target?.kind === 'product' && (
        <ProductFormDialog
          open={targetOpen}
          onOpenChange={setTargetOpen}
          product={target.product}
          initialTab={target.tab ?? 'details'}
          onSaved={() => {
            void getProducts.trigger(undefined, { skipCache: true })
            window.dispatchEvent(new Event(PRODUCTS_CHANGED))
          }}
        />
      )}
      {target?.kind === 'recipe' && (
        <RecipeDialog
          product={null}
          flavorName={target.flavorName}
          open={targetOpen}
          onOpenChange={setTargetOpen}
          onSaved={() => window.dispatchEvent(new Event(PRODUCTS_CHANGED))}
        />
      )}
      {target?.kind === 'ingredient' && (
        <IngredientFormDialog
          ingredient={target.ingredient}
          existingNames={ingredientsList.map(i => i.name)}
          categories={categories}
          open={targetOpen}
          onOpenChange={setTargetOpen}
          onSaved={() => window.dispatchEvent(new Event(INGREDIENTS_CHANGED))}
        />
      )}
    </>
  )
}
